import React from "react";

import {
  Button,
  FloatingLabelInput,
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components";
import { toast } from "@/components/ui/use-toast";
import { useI18n } from "@/context/i18n";

import type { TCampaign } from "../../campaigns.types";

export interface ICampaignMarketingCostSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  campaign: TCampaign | null;
  isSaving: boolean;
  onSave: (payload: {
    whatsapp_unit_cost_cents: number | null;
    whatsapp_budget_cents: number | null;
  }) => void;
}

function parseReaisToCents(raw: string): number | null {
  const s = raw.replace(/\s/g, "").replace(",", ".");
  if (!s) {
    return null;
  }
  const n = Number(s);
  if (!Number.isFinite(n) || n < 0) {
    return null;
  }
  return Math.round(n * 100);
}

function centsToReaisInput(cents: number | null | undefined): string {
  if (cents == null) {
    return "";
  }
  return (cents / 100).toFixed(2).replace(".", ",");
}

export const CampaignMarketingCostSheet: React.FC<
  ICampaignMarketingCostSheetProps
> = ({ open, onOpenChange, campaign, isSaving, onSave }) => {
  const { t } = useI18n();
  const [unit, setUnit] = React.useState("");
  const [budget, setBudget] = React.useState("");

  React.useEffect(() => {
    if (!open || !campaign) {
      return;
    }
    setUnit(centsToReaisInput(campaign.whatsapp_unit_cost_cents));
    setBudget(centsToReaisInput(campaign.whatsapp_budget_cents));
  }, [open, campaign]);

  const handleSubmit = React.useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();
      const unitCents = parseReaisToCents(unit);
      const budgetCents = parseReaisToCents(budget);
      if (unit.trim() && unitCents == null) {
        toast({
          title: t("generic.error"),
          description: t("campaigns.dashboard.costSheet.invalidMoney"),
          variant: "destructive",
        });
        return;
      }
      if (budget.trim() && budgetCents == null) {
        toast({
          title: t("generic.error"),
          description: t("campaigns.dashboard.costSheet.invalidMoney"),
          variant: "destructive",
        });
        return;
      }
      onSave({
        whatsapp_unit_cost_cents: unitCents,
        whatsapp_budget_cents: budgetCents,
      });
    },
    [unit, budget, onSave, t]
  );

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="sm:max-w-md">
        <SheetHeader>
          <SheetTitle>{t("campaigns.dashboard.costSheet.title")}</SheetTitle>
        </SheetHeader>
        <form onSubmit={handleSubmit} className="mt-6 space-y-4">
          <FloatingLabelInput
            label={t("campaigns.dashboard.costSheet.unit")}
            placeholder="0,44"
            value={unit}
            onChange={(e) => setUnit(e.target.value)}
            autoComplete="off"
          />
          <FloatingLabelInput
            label={t("campaigns.dashboard.costSheet.budget")}
            placeholder="500,00"
            value={budget}
            onChange={(e) => setBudget(e.target.value)}
            autoComplete="off"
          />
          <Button type="submit" className="w-full" disabled={isSaving}>
            {t("campaigns.dashboard.costSheet.save")}
          </Button>
        </form>
      </SheetContent>
    </Sheet>
  );
};
